import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Loader2, Lock, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useManageProcessFieldDefs } from '@/hooks/useProcessSteps'
import { DATA_TYPE_LABELS } from '@/lib/fieldValidation'
import {
  ATTRIBUTE_TYPES,
  ATTRIBUTE_TYPE_LABELS,
  attributeDataTypes,
  attributeType,
  describeAttributeType,
  METHOD_ATTRIBUTE_KEY,
  type AttributeType,
} from '@/lib/processAttributes'
import { keyFromLabel, summariseDef } from '@/lib/processAttributeDefs'
import type { FieldDataType } from '@/types/inventory'
import type { ProcessFieldDef } from '@/api/processStepsApi'

/**
 * Where an attribute gets DEFINED.
 *
 * The Field Attributes panel edits a step's values; this edits the set those
 * values are drawn from — the definitions every step shares. Opened from that
 * panel rather than living under Configure, because the need surfaces while
 * filling one in, and it needs no new navigation to reach.
 *
 * The **Method** definition is locked: its options come from the methods table
 * and its values are membership in `MethodProcessStep`, so a changed data type
 * or option list would describe something the editor does not do. It is shown
 * rather than hidden, so the set on screen is the set that exists.
 */
// Per-user column layout for the attribute-definition table. Attribute is
// locked (it names the definition) and so is the actions cell, which is the
// only route to edit or delete. See src/components/table/index.ts.
//
// The DefRow sub-component below calls the same hook, so the header and the
// rows read one layout out of TableColumnPrefsProvider.
const ATTR_DEFS_TABLE_ID = 'process-attribute-defs'

const ATTR_DEFS_COLUMNS: TableColumnDef[] = [
  { id: 'attribute', header: 'Attribute', locked: true, sortable: true },
  { id: 'type', header: 'Type', sortable: true },
  { id: 'options', header: 'Options / rules', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Column sorting (z8nrz7e4h6) — the display order only; a definition's stored
// `order` is not touched.
const ATTR_DEFS_SORT: SortAccessors<ProcessFieldDef> = {
  attribute: d => d.label,
  type: d => describeAttributeType(d, DATA_TYPE_LABELS),
  options: d => summariseDef(d),
}

/*
 * Percentages, kept rather than converted to pixel defaults: they keep filling
 * the dialog at any width. A dragged width overrides them inline.
 */
const ATTR_DEF_HEAD_CLASSES: Record<string, string> = {
  attribute: 'w-[26%]',
  type: 'w-[20%]',
  options: 'w-[32%]',
  actions: 'w-[22%]',
}

export function ProcessAttributeDefsDialog({
  open,
  onClose,
  defs,
  canManage,
}: {
  open: boolean
  onClose: () => void
  defs: ProcessFieldDef[]
  canManage: boolean
}) {
  const cols = useTableColumns(ATTR_DEFS_TABLE_ID, ATTR_DEFS_COLUMNS)
  const { create, update, remove } = useManageProcessFieldDefs()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<ProcessFieldDef | null>(null)
  const [error, setError] = useState<string | null>(null)
  const busy = create.isPending || update.isPending || remove.isPending
  const rows = useSortedRows(defs, cols.sort, ATTR_DEFS_SORT)

  function reset() {
    setAdding(false)
    setEditingId(null)
    setConfirming(null)
    setError(null)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) {
          reset()
          onClose()
        }
      }}
    >
      {/* Wider than the default: five columns of controls, and Type was squeezed
          to ~100px at 3xl — enough for "Yes / No" with nothing to spare. */}
      <DialogContent className='sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Process attributes</DialogTitle>
          <DialogDescription>
            What every process step can record. Every attribute is one of two types
            — a <span className='font-medium'>Flag</span>, which records yes or no,
            or a <span className='font-medium'>Value</span>, which records what is
            entered — and the type decides which column of the step's panel it is
            answered in.
          </DialogDescription>
        </DialogHeader>

        {canManage && (
          <div>
            <Button
              size='sm'
              className='gap-1.5'
              disabled={adding || busy}
              onClick={() => {
                setError(null)
                setEditingId(null)
                setAdding(true)
              }}
            >
              <Plus className='h-4 w-4' />
              Add attribute
            </Button>
          </div>
        )}
        {error && <p className='text-sm font-medium text-red-600'>{error}</p>}

        <div className='overflow-x-auto rounded-lg border'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={ATTR_DEF_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {adding && (
                <DefRow
                  editing
                  saveLabel='Add'
                  onCancel={() => setAdding(false)}
                  onSave={async payload => {
                    setError(null)
                    try {
                      await create.mutateAsync(payload)
                      setAdding(false)
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Could not add that attribute.')
                    }
                  }}
                />
              )}

              {rows.map(def =>
                editingId === def.id ? (
                  <DefRow
                    key={def.id}
                    def={def}
                    editing
                    onCancel={() => setEditingId(null)}
                    onSave={async payload => {
                      setError(null)
                      try {
                        // `key` never changes: stored values are keyed on it.
                        const { key: _key, ...rest } = payload
                        await update.mutateAsync({ id: def.id, payload: rest })
                        setEditingId(null)
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Could not save that attribute.')
                      }
                    }}
                  />
                ) : (
                  // `align-top` on every cell, not the inherited `align-middle`:
                  // the first cell is two lines (label over key), so centring the
                  // others floated them below the label they belong to.
                  <TableRow key={def.id}>
                    <TableCell className='align-top'>
                      <div className='font-medium'>{def.label}</div>
                      <div className='font-mono text-xs text-muted-foreground'>{def.key}</div>
                    </TableCell>
                    <TableCell className='align-top text-sm'>
                      {describeAttributeType(def, DATA_TYPE_LABELS)}
                    </TableCell>
                    <TableCell className='align-top text-sm text-muted-foreground'>
                      {summariseDef(def) ?? '—'}
                    </TableCell>
                    <TableCell className='align-top text-right'>
                      {def.key === METHOD_ATTRIBUTE_KEY ? (
                        <span
                          className='inline-flex items-center gap-1 text-xs text-muted-foreground'
                          title='Its options come from the methods table and its values are membership, so it cannot be redefined here.'
                        >
                          <Lock className='h-3 w-3' />
                          Built in
                        </span>
                      ) : (
                        canManage && (
                          <div className='flex justify-end gap-2'>
                            {confirming?.id === def.id ? (
                              <>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  onClick={() => setConfirming(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant='destructive'
                                  size='sm'
                                  disabled={busy}
                                  onClick={() => void doRemove(def)}
                                >
                                  Delete
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  disabled={busy}
                                  onClick={() => {
                                    setError(null)
                                    setAdding(false)
                                    setEditingId(def.id)
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  disabled={busy}
                                  onClick={() => setConfirming(def)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>

        {/* Says what deleting costs before it is clicked. The values stay in each
            step's map and stop rendering, because a value no definition covers is
            dropped on read. */}
        {confirming && (
          <p className='text-sm text-amber-800'>
            Deleting <span className='font-medium'>{confirming.label}</span> leaves any value
            already recorded against it unreadable.
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={busy}>
            {busy && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  async function doRemove(def: ProcessFieldDef) {
    setError(null)
    try {
      const { orphaned } = await remove.mutateAsync(def.id)
      setConfirming(null)
      if (orphaned > 0) {
        setError(
          `Deleted. ${orphaned} process ${orphaned === 1 ? 'step held' : 'steps held'} a value for it; those values are no longer shown.`,
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete that attribute.')
      setConfirming(null)
    }
  }
}

const DATA_TYPES = Object.keys(DATA_TYPE_LABELS) as FieldDataType[]

/**
 * An attribute's TYPE is the first thing an author picks, because it is the
 * question the attribute asks — yes/no, or a value — and everything else
 * follows from it.
 *
 * A Flag needs no second pick: YES/NO is what recording yes or no means, so
 * the type sets the data type and the shape select is not offered. A Value
 * needs to know what shape the value takes, and cannot be YES/NO — that is a
 * Flag, and offering it under both would put the same attribute in two columns.
 */
function defaultDataType(type: AttributeType, current: FieldDataType): FieldDataType {
  const allowed = attributeDataTypes(type, DATA_TYPES)
  return allowed.includes(current) ? current : allowed[0]
}

/** One definition, being added or changed. */
function DefRow({
  def,
  editing,
  saveLabel = 'Save',
  onCancel,
  onSave,
}: {
  def?: ProcessFieldDef
  editing: boolean
  saveLabel?: string
  onCancel: () => void
  onSave: (payload: {
    key: string
    label: string
    dataType: FieldDataType
    options?: string[]
    validation?: { required?: boolean } | null
  }) => Promise<void>
}) {
  // Same table id as the header above, so the two stay in step.
  const cols = useTableColumns(ATTR_DEFS_TABLE_ID, ATTR_DEFS_COLUMNS)
  const [label, setLabel] = useState(def?.label ?? '')
  const [type, setType] = useState<AttributeType>(
    def ? attributeType(def) : 'attribute-value',
  )
  const [dataType, setDataType] = useState<FieldDataType>(def?.dataType ?? 'string')
  const [optionText, setOptionText] = useState((def?.options ?? []).join(', '))
  const [required, setRequired] = useState(def?.validation?.required === true)

  const options = optionText
    .split(',')
    .map(o => o.trim())
    .filter(Boolean)
  // The key is derived from the label on ADD and frozen afterwards, so an author
  // never types a machine key and a rename can never orphan stored values.
  const key = def?.key ?? keyFromLabel(label)
  const enumEmpty = type === 'attribute-value' && dataType === 'enum' && options.length === 0

  if (!editing) return null

  /**
   * Every control in this row sits in the SAME 36px band, top-aligned.
   *
   * Two things were wrong. The cells inherited `align-middle`, so the taller
   * first cell — the name input plus the reserved key line beneath it — centred
   * its siblings and left the input sitting above them. And `Input` is `h-10`
   * while `SearchableSelect` is `h-9`, so the name field was also four pixels
   * taller than the type picker beside it.
   *
   * The inputs are pinned to `h-9` to match the select, and anything shorter
   * than that (the checkbox, the buttons) is centred inside a band of the same
   * height rather than pinned to the cell's top edge, which would leave it
   * riding above the inputs' text.
   */
  const band = 'flex h-9 items-center'

  return (
    <TableRow>

      <ColumnCells
        columns={cols}
        cells={{
          attribute: (
            <TableCell className='align-top'>
              <Input
                className='h-9'
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder='Attribute name'
              />
              {/* Reserved rather than conditional: the row would jump by a line the
                  moment the first character is typed. */}
              <div className={cn('mt-1 font-mono text-xs', key ? 'text-muted-foreground' : 'invisible')}>
                {key || 'key'}
              </div>
            </TableCell>
          ),
          type: (
            <TableCell className='align-top'>
              <div className='space-y-1'>
                <SearchableSelect
                  value={type}
                  onChange={v => {
                    const next = v as AttributeType
                    setType(next)
                    setDataType(defaultDataType(next, dataType))
                  }}
                  options={ATTRIBUTE_TYPES.map(t => ({ value: t, label: ATTRIBUTE_TYPE_LABELS[t] }))}
                  placeholder='Type'
                />
                {/* Only a Value has a shape to choose; a Flag's is what its type
                    means. Rendered in the same cell so the two reading as one answer
                    is what the layout says. */}
                {type === 'attribute-value' && (
                  <SearchableSelect
                    value={dataType}
                    onChange={v => setDataType(v as FieldDataType)}
                    options={attributeDataTypes(type, DATA_TYPES).map(t => ({
                      value: t,
                      label: DATA_TYPE_LABELS[t],
                    }))}
                    placeholder='Value shape'
                  />
                )}
              </div>
            </TableCell>
          ),
          options: (
            <TableCell className='align-top'>
              {type === 'attribute-flag' ? (
                // A flag has no rule to set: it is yes or no, and both are answers.
                <span className={cn(band, 'text-sm text-muted-foreground')}>Yes or no</span>
              ) : dataType === 'enum' ? (
                <Input
                  className='h-9'
                  value={optionText}
                  onChange={e => setOptionText(e.target.value)}
                  placeholder='Bag, Vial, Cryobag'
                />
              ) : (
                <label className={cn(band, 'cursor-pointer gap-2 text-sm')}>
                  <Checkbox checked={required} onCheckedChange={() => setRequired(r => !r)} />
                  Required
                </label>
              )}
            </TableCell>
          ),
          actions: (
            <TableCell className='align-top text-right'>
              <div className={cn(band, 'justify-end gap-2')}>
                <Button variant='outline' size='sm' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  size='sm'
                  disabled={!label.trim() || !key || enumEmpty}
                  onClick={() =>
                    void onSave({
                      key,
                      label: label.trim(),
                      dataType,
                      ...(dataType === 'enum' ? { options } : {}),
                      // Only `required` is offered here; the finer rules (min, max,
                      // pattern) are accepted by the API and shown read-only, rather
                      // than making this dialog a form builder. A flag takes none of
                      // them: NO is an answer, so "required" could never fail.
                      validation:
                        type === 'attribute-flag' || dataType === 'enum'
                          ? null
                          : required
                            ? { required: true }
                            : null,
                    })
                  }
                >
                  {saveLabel}
                </Button>
              </div>
            </TableCell>
          ),
        }}
      />
    </TableRow>
  )
}
